'use client';

/**
 * Offline Indicator Component
 * 
 * Shows connection status and offline capabilities
 * Provides user feedback for PWA offline state
 */

import React, { useState, useEffect } from 'react';
import { AlertTriangle, Wifi, WifiOff, RefreshCw } from 'lucide-react';

interface OfflineIndicatorProps {
  className?: string;
  showWhenOnline?: boolean;
  onSyncRequest?: () => void;
}

interface ConnectionState {
  isOnline: boolean;
  isOfflineCapable: boolean;
  pendingSync: boolean;
  lastOnline: Date | null;
}

const OFFLINE_ENABLED = process.env.NEXT_PUBLIC_ENABLE_OFFLINE_WORKSPACE === 'true';

export function OfflineIndicator({ 
  className = '',
  showWhenOnline = false,
  onSyncRequest
}: OfflineIndicatorProps) {
  if (!OFFLINE_ENABLED) {
    return null;
  }

  const [connectionState, setConnectionState] = useState<ConnectionState>({
    isOnline: true,
    isOfflineCapable: false,
    pendingSync: false,
    lastOnline: null
  });

  // Check offline capabilities
  useEffect(() => {
    const checkOfflineCapabilities = async () => {
      const hasServiceWorker = 'serviceWorker' in navigator;
      const hasIndexedDB = 'indexedDB' in window;
      const hasCache = 'caches' in window;
      
      const isOfflineCapable = hasServiceWorker && hasIndexedDB && hasCache;
      
      setConnectionState(prev => ({
        ...prev,
        isOfflineCapable
      }));
    };

    checkOfflineCapabilities();
  }, []);

  // Monitor online/offline status
  useEffect(() => {
    const updateOnlineStatus = () => {
      setConnectionState(prev => {
        const isOnline = navigator.onLine;

        // If coming back online and there might be pending data
        if (isOnline && !prev.isOnline) {
          onSyncRequest?.();
        }

        return {
          ...prev,
          isOnline,
          lastOnline: isOnline ? new Date() : prev.lastOnline
        };
      });
    };

    // Set initial state
    updateOnlineStatus();

    // Listen for online/offline events
    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  }, [onSyncRequest]);

  // Check for pending sync data (placeholder for sync manager)
  useEffect(() => {
    const checkPendingSync = async () => {
      // This will be implemented with the sync manager
      // For now, simulate based on offline state
      const hasPendingData = !connectionState.isOnline && connectionState.isOfflineCapable;
      
      setConnectionState(prev => ({
        ...prev,
        pendingSync: hasPendingData
      }));
    };

    checkPendingSync();
  }, [connectionState.isOnline, connectionState.isOfflineCapable]);

  // Don't show anything if online and showWhenOnline is false
  if (connectionState.isOnline && !showWhenOnline) {
    return null;
  }

  const getIndicatorStyle = () => {
    if (!connectionState.isOnline) {
      return 'bg-amber-100 text-amber-800 border-amber-200';
    }
    if (connectionState.pendingSync) {
      return 'bg-blue-100 text-blue-800 border-blue-200';
    }
    return 'bg-green-100 text-green-800 border-green-200';
  };

  const getStatusIcon = () => {
    if (!connectionState.isOnline) {
      return <WifiOff className="h-4 w-4" />;
    }
    if (connectionState.pendingSync) {
      return <RefreshCw className="h-4 w-4 animate-spin" />;
    }
    return <Wifi className="h-4 w-4" />;
  };

  const getStatusText = () => {
    if (!connectionState.isOnline) {
      if (connectionState.isOfflineCapable) {
        return 'Offline: cached lookups ready';
      }
      return 'Offline: limited functionality';
    }
    
    if (connectionState.pendingSync) {
      return 'Syncing offline changes…';
    }
    
    return 'Online and ready';
  };

  const getLastOnlineText = () => {
    if (connectionState.isOnline || !connectionState.lastOnline) {
      return null;
    }

    const timeSince = Date.now() - connectionState.lastOnline.getTime();
    const minutes = Math.floor(timeSince / 60000);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `Last online ${hours}h ago`;
    }
    if (minutes > 0) {
      return `Last online ${minutes}m ago`;
    }
    return 'Just went offline';
  };

  const handleSyncClick = () => {
    if (connectionState.isOnline && onSyncRequest) {
      onSyncRequest();
    }
  };

  return (
    <div className={`
      fixed top-4 right-4 z-50
      flex items-center gap-2 px-3 py-2
      border rounded-lg shadow-sm
      text-sm font-medium
      transition-all duration-200
      ${getIndicatorStyle()}
      ${className}
    `}>
      {getStatusIcon()}
      
      <div className="flex flex-col">
        <span>{getStatusText()}</span>
        {getLastOnlineText() && (
          <span className="text-xs opacity-75">
            {getLastOnlineText()}
          </span>
        )}
      </div>

      {/* Sync button for pending data */}
      {connectionState.isOnline && connectionState.pendingSync && (
        <button
          onClick={handleSyncClick}
          className="ml-2 px-2 py-1 text-xs bg-white bg-opacity-20 rounded hover:bg-opacity-30 transition-colors"
          title="Sync now"
        >
          Sync
        </button>
      )}

      {/* Warning icon for limited offline functionality */}
      {!connectionState.isOnline && !connectionState.isOfflineCapable && (
        <AlertTriangle className="h-4 w-4 ml-1" />
      )}
    </div>
  );
}

// Hook for accessing connection state
export function useConnectionState() {
  const [isOnline, setIsOnline] = useState(true);
  const [isOfflineCapable, setIsOfflineCapable] = useState(false);

  useEffect(() => {
    // Check initial state
    setIsOnline(navigator.onLine);

    // Check offline capabilities
    const checkCapabilities = async () => {
      const hasServiceWorker = 'serviceWorker' in navigator;
      const hasIndexedDB = 'indexedDB' in window;
      const hasCache = 'caches' in window;
      
      setIsOfflineCapable(hasServiceWorker && hasIndexedDB && hasCache);
    };

    checkCapabilities();

    // Listen for online/offline events
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return {
    isOnline,
    isOfflineCapable,
    canWorkOffline: isOfflineCapable
  };
}
